import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './TopBar.css';

// The tool set, shown in the "Verktyg" dropdown. Order matches the Dashboard
// tiles. Most tools are ungated (every user can open them). A `perm` gates an
// entry to users who have that permission (admins bypass) — keep the perm
// strings identical to the Dashboard tile gates. `tip` is a key in
// tooltips/tips.js.
const OVERVIEW = { to: '/dashboard', label: 'Alla verktyg', tip: 'nav.tools' };
const TOOLS = [
  { to: '/walking-calculator', label: 'Promenadavstånd', tip: 'nav.walking' },
  { to: '/street-analysis', label: 'Gatuanalys', tip: 'nav.street' },
  { to: '/open-space', label: 'Tomma ytor', perm: 'open_space:view', tip: 'nav.openSpace' },
  { to: '/space-syntax', label: 'Gatunät', perm: 'space_syntax:view', tip: 'nav.spaceSyntax' },
  { to: '/dp-analysis', label: 'DP-Analys', tip: 'nav.dp' },
  { to: '/urban-wiki', label: 'Urban Wiki', perm: 'urban_wiki:view', tip: 'nav.wiki' },
];

const TopBar = () => {
  const { user, logout, hasPermission } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const tools = TOOLS.filter((tool) => !tool.perm || hasPermission(tool.perm));
  const currentTool = tools.find((tool) => tool.to === pathname);

  // Land on the public landing page, not /login. Without the explicit
  // navigate, clearing auth state lets ProtectedRoute redirect to /login.
  // AuthContext cannot do this itself: AuthProvider is mounted outside the
  // Router in index.js, so it has no navigate.
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // The warm paper ground belongs to themed routes only — Login and Admin are
  // still on the old blue palette and would clash with it. TopBar owns the
  // class because it renders on exactly the themed routes.
  useEffect(() => {
    document.body.classList.add('ut-themed');
    return () => document.body.classList.remove('ut-themed');
  }, []);

  // Close the mobile menu on navigation, otherwise it stays open over the new page.
  useEffect(() => {
    setMenuOpen(false);
    setToolsOpen(false);
  }, [pathname]);

  // Close the tools dropdown on outside click or Escape.
  useEffect(() => {
    if (!toolsOpen) return undefined;
    const onDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setToolsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setToolsOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  return (
    <header className="ut-topbar">
      <div className="ut-topbar-inner">
        <Link to="/" className="ut-brand" data-tip="nav.brand">
          <svg className="ut-mark" viewBox="0 0 34 28" aria-hidden="true">
            <ellipse cx="12" cy="14" rx="10" ry="11" fill="#1B5E5A" opacity="0.88" />
            <ellipse cx="21" cy="11" rx="10" ry="11" fill="#C2683E" opacity="0.82" />
            <ellipse cx="17" cy="18" rx="9.5" ry="10" fill="#5B8C5A" opacity="0.82" />
          </svg>
          <span>Hälsosam Urbanism<span className="ut-brand-dot">.</span></span>
        </Link>

        <button
          className="ut-menu-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-label="Meny"
          data-tip="nav.menu"
        >
          <span className="ut-menu-bars" aria-hidden="true"></span>
        </button>

        <nav className={`ut-nav ${menuOpen ? 'is-open' : ''}`}>
          <div className={`ut-tools ${toolsOpen ? 'is-open' : ''}`} ref={dropdownRef}>
            <button
              type="button"
              className={`ut-tool-link ut-tools-toggle ${currentTool || pathname === OVERVIEW.to ? 'active' : ''}`}
              onClick={() => setToolsOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={toolsOpen}
              data-tip="nav.toolsMenu"
            >
              {currentTool ? currentTool.label : 'Verktyg'}
              <span className="ut-caret" aria-hidden="true" />
            </button>
            <div className="ut-tools-menu" role="menu">
              {[OVERVIEW, ...tools].map((tool) => (
                <Link
                  key={tool.to}
                  to={tool.to}
                  role="menuitem"
                  className={`ut-tools-item ${pathname === tool.to ? 'active' : ''} ${tool === OVERVIEW ? 'is-overview' : ''}`}
                  aria-current={pathname === tool.to ? 'page' : undefined}
                  data-tip={tool.tip}
                >
                  {tool.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="ut-user">
            <span className="ut-user-id" data-tip={user?.userId ? `Inloggad som ${user.userId}` : undefined}>{user?.userId}</span>
            {user?.role === 'admin' && (
              <Link to="/admin" className="ut-admin-link" data-tip="nav.admin">Admin</Link>
            )}
            <button onClick={handleLogout} className="ut-logout" data-tip="nav.logout">Logga ut</button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default TopBar;
